import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PdfLineItem {
  markId: string
  tagLabel: string | null
  description: string
  componentCategory: string
  componentSubcategory: string
  quantity: number
  unit: string
  laborHrsPerUnit: number
  directLaborUsd: number
  directMaterialUsd: number
  totalDirectUsd: number
}

export interface PdfEstimateData {
  projectName: string
  estimateNumber: string
  date: string
  grouped: Record<string, PdfLineItem[]>
  directLaborUsd: number
  directMaterialUsd: number
  directCostUsd: number
  laborBurdenPct: number
  laborBurdenUsd: number
  overheadPct: number
  overheadUsd: number
  profitPct: number
  profitUsd: number
  additionalWorkUsd: number
  subcontractorUsd: number
  equipmentSupplierUsd: number
  grandTotal: number
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 8,
    color: '#1A1612',
    backgroundColor: '#FDFAF5',
    paddingTop: 36,
    paddingBottom: 48,
    paddingHorizontal: 36,
  },
  // Header
  header: { marginBottom: 20 },
  headerTop: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 },
  projectName: { fontSize: 16, fontFamily: 'Helvetica-Bold', color: '#1B5E3B' },
  estimateNum: { fontSize: 10, color: '#5C5550', marginTop: 2 },
  headerRight: { alignItems: 'flex-end' },
  dateLabel: { fontSize: 7, color: '#5C5550', textTransform: 'uppercase', letterSpacing: 0.5 },
  dateValue: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginTop: 1 },
  divider: { height: 1.5, backgroundColor: '#1B5E3B', marginTop: 8 },
  // Table
  table: { marginBottom: 16 },
  colHeader: { flexDirection: 'row', backgroundColor: '#1A1612', paddingVertical: 5, paddingHorizontal: 6, marginBottom: 1 },
  colHeaderText: { color: '#FDFAF5', fontFamily: 'Helvetica-Bold', fontSize: 7, textTransform: 'uppercase', letterSpacing: 0.4 },
  categoryRow: { flexDirection: 'row', paddingVertical: 5, paddingHorizontal: 6, marginTop: 6, marginBottom: 1 },
  categoryLabel: { fontFamily: 'Helvetica-Bold', fontSize: 8, textTransform: 'uppercase', letterSpacing: 0.5 },
  dataRow: { flexDirection: 'row', paddingVertical: 4, paddingHorizontal: 6, borderBottomWidth: 0.5, borderBottomColor: '#E2D9CC' },
  dataRowAlt: { backgroundColor: '#F8F4EE' },
  subtotalRow: { flexDirection: 'row', paddingVertical: 4, paddingHorizontal: 6, borderTopWidth: 1, borderTopColor: '#1A1612', marginTop: 1, marginBottom: 8 },
  subtotalText: { fontFamily: 'Helvetica-Bold', fontSize: 7.5, color: '#1A1612' },
  // Columns widths (points, page width ~522pt after padding)
  colDesc:    { flex: 1 },
  colQty:     { width: 30, textAlign: 'right' },
  colUnit:    { width: 28, textAlign: 'center' },
  colLabHrs:  { width: 40, textAlign: 'right' },
  colMat:     { width: 52, textAlign: 'right' },
  colTotal:   { width: 58, textAlign: 'right' },
  // Totals section
  totalsBox: { marginTop: 8, alignSelf: 'flex-end', width: 240, borderWidth: 1, borderColor: '#E2D9CC', borderRadius: 4, overflow: 'hidden' },
  totalsRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 5, paddingHorizontal: 10, borderBottomWidth: 0.5, borderBottomColor: '#E2D9CC' },
  totalsLabel: { fontSize: 8, color: '#5C5550' },
  totalsValue: { fontSize: 8, fontFamily: 'Helvetica-Bold' },
  totalsGrandRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 7, paddingHorizontal: 10, backgroundColor: '#1B5E3B' },
  totalsGrandLabel: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#FDFAF5' },
  totalsGrandValue: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#FDFAF5' },
  // Footer
  footer: { position: 'absolute', bottom: 24, left: 36, right: 36, flexDirection: 'row', justifyContent: 'space-between', borderTopWidth: 0.5, borderTopColor: '#E2D9CC', paddingTop: 6 },
  footerText: { fontSize: 6.5, color: '#5C5550' },
})

// ── Helpers ───────────────────────────────────────────────────────────────────

function usd(n: number) {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function pct(n: number) {
  return `${Math.round(n * 100)}%`
}

function unitLabel(u: string) {
  if (u === 'SQ_FT') return 'sf'
  return u.toLowerCase()
}

const CAT_COLOR: Record<string, string> = {
  Ductwork:  '#1D6FAE',
  Piping:    '#0E7490',
  Equipment: '#C2460A',
  Controls:  '#6D3FA8',
}

// ── Document ──────────────────────────────────────────────────────────────────

export function EstimatePdf({ data }: { data: PdfEstimateData }) {
  const categoryOrder = ['Ductwork', 'Equipment', 'Piping', 'Controls']
  const orderedCategories = [
    ...categoryOrder.filter((c) => data.grouped[c]),
    ...Object.keys(data.grouped).filter((c) => !categoryOrder.includes(c)),
  ]

  return (
    <Document title={`${data.estimateNumber} — ${data.projectName}`}>
      <Page size="LETTER" style={S.page}>

        {/* ── Header ── */}
        <View style={S.header}>
          <View style={S.headerTop}>
            <View>
              <Text style={S.projectName}>{data.projectName}</Text>
              <Text style={S.estimateNum}>{data.estimateNumber}</Text>
            </View>
            <View style={S.headerRight}>
              <Text style={S.dateLabel}>Date</Text>
              <Text style={S.dateValue}>{data.date}</Text>
            </View>
          </View>
          <View style={S.divider} />
        </View>

        {/* ── Line-item table ── */}
        <View style={S.table}>
          {/* Column headers */}
          <View style={S.colHeader}>
            <Text style={[S.colHeaderText, S.colDesc]}>Description</Text>
            <Text style={[S.colHeaderText, S.colQty]}>Qty</Text>
            <Text style={[S.colHeaderText, S.colUnit]}>Unit</Text>
            <Text style={[S.colHeaderText, S.colLabHrs]}>Lab Hrs</Text>
            <Text style={[S.colHeaderText, S.colMat]}>Material</Text>
            <Text style={[S.colHeaderText, S.colTotal]}>Total</Text>
          </View>

          {orderedCategories.map((cat) => {
            const items = data.grouped[cat] ?? []
            const catColor = CAT_COLOR[cat] ?? '#5C5550'
            const catLabHrs = items.reduce((s, i) => s + i.laborHrsPerUnit * i.quantity, 0)
            const catMat    = items.reduce((s, i) => s + i.directMaterialUsd, 0)
            const catTotal  = items.reduce((s, i) => s + i.totalDirectUsd, 0)

            return (
              <View key={cat}>
                {/* Category header */}
                <View style={[S.categoryRow, { backgroundColor: catColor + '18' }]}>
                  <Text style={[S.categoryLabel, { color: catColor, flex: 1 }]}>{cat}</Text>
                </View>

                {/* Line items */}
                {items.map((item, idx) => (
                  <View key={item.markId} style={[S.dataRow, idx % 2 === 1 ? S.dataRowAlt : {}]}>
                    <View style={S.colDesc}>
                      <Text>{item.description}</Text>
                      {item.tagLabel && (
                        <Text style={{ fontSize: 6.5, color: '#5C5550', marginTop: 1 }}>
                          Tag: {item.tagLabel}
                        </Text>
                      )}
                    </View>
                    <Text style={S.colQty}>{item.quantity}</Text>
                    <Text style={S.colUnit}>{unitLabel(item.unit)}</Text>
                    <Text style={S.colLabHrs}>{(item.laborHrsPerUnit * item.quantity).toFixed(1)}</Text>
                    <Text style={S.colMat}>{usd(item.directMaterialUsd)}</Text>
                    <Text style={S.colTotal}>{usd(item.totalDirectUsd)}</Text>
                  </View>
                ))}

                {/* Category subtotal */}
                <View style={S.subtotalRow}>
                  <Text style={[S.subtotalText, S.colDesc]}>{cat} Subtotal</Text>
                  <Text style={[S.subtotalText, S.colQty]} />
                  <Text style={[S.subtotalText, S.colUnit]} />
                  <Text style={[S.subtotalText, S.colLabHrs]}>{catLabHrs.toFixed(1)}</Text>
                  <Text style={[S.subtotalText, S.colMat]}>{usd(catMat)}</Text>
                  <Text style={[S.subtotalText, S.colTotal]}>{usd(catTotal)}</Text>
                </View>
              </View>
            )
          })}
        </View>

        {/* ── Totals box ── */}
        <View style={S.totalsBox}>
          <View style={S.totalsRow}>
            <Text style={S.totalsLabel}>Direct Labor</Text>
            <Text style={S.totalsValue}>{usd(data.directLaborUsd)}</Text>
          </View>
          <View style={S.totalsRow}>
            <Text style={S.totalsLabel}>Direct Material</Text>
            <Text style={S.totalsValue}>{usd(data.directMaterialUsd)}</Text>
          </View>
          <View style={S.totalsRow}>
            <Text style={S.totalsLabel}>Direct Cost</Text>
            <Text style={S.totalsValue}>{usd(data.directCostUsd)}</Text>
          </View>
          <View style={S.totalsRow}>
            <Text style={S.totalsLabel}>Labor Burden ({pct(data.laborBurdenPct)})</Text>
            <Text style={S.totalsValue}>{usd(data.laborBurdenUsd)}</Text>
          </View>
          <View style={S.totalsRow}>
            <Text style={S.totalsLabel}>Overhead ({pct(data.overheadPct)})</Text>
            <Text style={S.totalsValue}>{usd(data.overheadUsd)}</Text>
          </View>
          <View style={S.totalsRow}>
            <Text style={S.totalsLabel}>Profit ({pct(data.profitPct)})</Text>
            <Text style={S.totalsValue}>{usd(data.profitUsd)}</Text>
          </View>
          {data.additionalWorkUsd > 0 && (
            <View style={S.totalsRow}>
              <Text style={S.totalsLabel}>Additional Work</Text>
              <Text style={S.totalsValue}>{usd(data.additionalWorkUsd)}</Text>
            </View>
          )}
          {data.subcontractorUsd > 0 && (
            <View style={S.totalsRow}>
              <Text style={S.totalsLabel}>Subcontractor</Text>
              <Text style={S.totalsValue}>{usd(data.subcontractorUsd)}</Text>
            </View>
          )}
          {data.equipmentSupplierUsd > 0 && (
            <View style={S.totalsRow}>
              <Text style={S.totalsLabel}>Equipment Supplier</Text>
              <Text style={S.totalsValue}>{usd(data.equipmentSupplierUsd)}</Text>
            </View>
          )}
          <View style={S.totalsGrandRow}>
            <Text style={S.totalsGrandLabel}>Grand Total</Text>
            <Text style={S.totalsGrandValue}>{usd(data.grandTotal)}</Text>
          </View>
        </View>

        {/* ── Footer ── */}
        <View style={S.footer} fixed>
          <Text style={S.footerText}>EstimatePro · {data.estimateNumber}</Text>
          <Text style={S.footerText} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
        </View>

      </Page>
    </Document>
  )
}
